import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar, type VariantType } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { ArgusNavigateBridge } from "../components/argus/argus-navigate-bridge";
import { ARGUS_SIDEBAR_WIDTH } from "../components/argus/argus-sidebar";
import { AskArgusPrompt } from "../components/argus/ask-argus-prompt";
import { Comments } from "../components/comments";
import { ErrorBoundary } from "../components/error-boundary";
import { FullScreenLoading } from "../components/full-screen-loading";
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
  Sidebar,
} from "../components/layouts/sidebar";
import { CommentsProvider, useCommentsContext } from "../contexts/comments-context";
import { useSupportChatContext } from "../contexts/support-chat-context";
import { useApiMutation, useOnboarded } from "../hooks";
import { useOrgFlags } from "../hooks/use-org-flags";
import { useSSE } from "../hooks/use-sse";
import { useArgusConnectionContext } from "../providers/argus-connection-provider";
import { useUserPreferences } from "../providers/user-preferences-provider";
import { SIDEBAR } from "../theme/theme";

const COMMENTS_DRAWER_WIDTH = 360;

type NotificationAction = {
  type: string;
  data?: Record<string, unknown>;
};

type AppEventData = {
  message: string;
  level: "info" | "warning" | "error" | "success";
  link?: string;
  action?: NotificationAction;
};

function LayoutContent() {
  const { commentsOpen } = useCommentsContext();
  const { sidebarOpen: argusSidebarOpen } = useArgusConnectionContext();
  const loc = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // xs: 0-600px
  const isSmallScreen = useMediaQuery(theme.breakpoints.between("sm", "lg")); // sm-md: 600-1200px
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg")); // lg+: 1200px+

  const { preferences, updatePreferences } = useUserPreferences();
  const [utmDialogOpen, setUtmDialogOpen] = useState(false);
  const { data: onboardedData, isLoading: isOnboardedLoading } = useOnboarded();
  const { isLoading: isFlagsLoading, isCompleted: flagsCompleted } = useOrgFlags();
  const {
    isOpen: isSupportChatOpen,
    viewingConversationId,
    openToConversation,
  } = useSupportChatContext();

  const { mutate: captureUtm } = useApiMutation<{ utm_query?: string }, { message: string }>(
    "POST",
    "/marketing/utm-capture",
  );

  // Generic action handler for notification actions
  const handleNotificationAction = useCallback(
    (action: NotificationAction) => {
      switch (action.type) {
        case "open_support_chat":
          if (action.data?.conversation_id) {
            openToConversation(action.data.conversation_id as string);
          }
          break;
        default:
          console.warn(`Unknown notification action type: ${action.type}`);
      }
    },
    [openToConversation],
  );

  // Global SSE listener for app events
  useSSE<AppEventData>("/app-events", {
    onEvent: (event) => {
      const { message, level, link, action } = event.data;

      // Skip notification if support chat is already open to this conversation
      if (
        action?.type === "open_support_chat" &&
        isSupportChatOpen &&
        viewingConversationId === action.data?.conversation_id
      ) {
        return;
      }

      // Determine button label based on action type
      const getActionLabel = (actionType?: string) => {
        if (actionType === "open_support_chat") return "Open Chat";
        return "View";
      };

      enqueueSnackbar(message, {
        variant: level as VariantType,
        autoHideDuration: 5_000,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
        action: (snackbarId) => (
          <>
            {action && (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  closeSnackbar(snackbarId);
                  handleNotificationAction(action);
                }}
              >
                {getActionLabel(action.type)}
              </Button>
            )}
            {link && !action && (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  closeSnackbar(snackbarId);
                  navigate(link);
                }}
              >
                View
              </Button>
            )}
            <IconButton size="small" color="inherit" onClick={() => closeSnackbar(snackbarId)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ),
      });
    },
  });

  // Decide: redirect to /onboarding, show loading, or render the dashboard.
  //   - While onboarded OR flags are still resolving: don't know yet (null)
  //   - Grandfathered orgs (pre-2025-12-05 / API returns onboarded=true): skip
  //   - Otherwise: DB flag `onboarding.completed_at` is source of truth
  const onboardingCompleted = useMemo(() => {
    if (isOnboardedLoading || isFlagsLoading) {
      return null; // still resolving
    }
    if (onboardedData?.onboarded) {
      return true; // grandfathered short-circuit
    }
    return flagsCompleted;
  }, [onboardedData?.onboarded, isOnboardedLoading, isFlagsLoading, flagsCompleted]);

  const shouldShowPricingPopup = useMemo(() => {
    const pending = sessionStorage.getItem("pricing_utm_pending") === "1";
    const shown = sessionStorage.getItem("pricing_utm_shown") === "1";
    const params = new URLSearchParams(loc.search);
    const src = params.get("utm_source");
    const content = params.get("utm_content");
    return (pending || (src === "pricing" && content === "cta_button")) && !shown;
  }, [loc.search]);

  useEffect(() => {
    // Trigger once per session to avoid repeated emails when navigating
    if (shouldShowPricingPopup) {
      setUtmDialogOpen(true);
      const stored = sessionStorage.getItem("pricing_utm_query") || loc.search || undefined;
      captureUtm({ utm_query: stored || undefined });
      sessionStorage.setItem("pricing_utm_captured", "1");
      sessionStorage.setItem("pricing_utm_shown", "1");
      sessionStorage.removeItem("pricing_utm_pending");
    }
  }, [shouldShowPricingPopup, loc.search, captureUtm]);

  useEffect(() => {
    if (!utmDialogOpen) return;
    // Fire a quick confetti celebration when dialog opens
    // Use dynamic import so the app works even if the lib isn't installed yet
    (async () => {
      try {
        const mod = await import("canvas-confetti");

        // Render confetti to a dedicated overlay canvas with a high z-index so it appears above the dialog
        const canvas = document.createElement("canvas");
        canvas.style.position = "fixed";
        canvas.style.top = "0";
        canvas.style.left = "0";
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        canvas.style.pointerEvents = "none";
        canvas.style.zIndex = "2147483647"; // above MUI dialogs
        document.body.appendChild(canvas);

        const confetti = mod.default.create(canvas, {
          resize: true,
          useWorker: true,
        });

        const bursts = [
          {
            delay: 0,
            opts: { particleCount: 120, spread: 70, origin: { y: 0.6 } },
          },
          {
            delay: 300,
            opts: {
              particleCount: 80,
              spread: 100,
              scalar: 0.9,
              origin: { y: 0.6 },
            },
          },
          {
            delay: 600,
            opts: {
              particleCount: 100,
              spread: 120,
              startVelocity: 35,
              origin: { y: 0.3 },
            },
          },
        ];

        bursts.forEach(({ delay, opts }) => {
          setTimeout(() => confetti(opts), delay);
        });

        // Cleanup the canvas after the animation
        setTimeout(() => {
          try {
            document.body.removeChild(canvas);
          } catch {
            // ignore removal issues
          }
        }, 5000);
      } catch {
        // ignore if canvas-confetti is not available
      }
    })();
  }, [utmDialogOpen]);

  // Auto-collapse sidebar when navigating to certain routes
  useEffect(() => {
    if (
      loc.pathname.startsWith("/generate") ||
      loc.pathname.startsWith("/discovery-review") ||
      loc.pathname.startsWith("/agent-sessions")
    ) {
      updatePreferences({ sidebarCollapsed: true });
    }
  }, [loc.pathname, updatePreferences]);

  // Set default collapse state based on screen size
  useEffect(() => {
    if (isSmallScreen) {
      // Small screens: collapsed by default
      updatePreferences({ sidebarCollapsed: true });
    } else if (isLargeScreen) {
      // Handle if we are on a large screen
    }
  }, [isSmallScreen, isLargeScreen, updatePreferences]);

  const handleToggleSidebar = () => {
    updatePreferences({ sidebarCollapsed: !preferences.sidebarCollapsed });
  };

  return (
    <Box
      sx={{
        height: "100vh",
        backgroundColor: SIDEBAR.bg, // bg-bg-secondary
        fontFamily:
          '"Geist", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        overflow: "hidden",
        paddingTop: isMobile ? "56px" : 0, // Add padding for mobile top bar
      }}
    >
      {/* Main layout container */}
      <Box
        sx={{
          display: "flex",
          height: isMobile ? "calc(100vh - 56px)" : "100vh",
        }}
      >
        {/* Sidebar container - hidden on mobile, shown on sm+ */}
        {!isMobile && (
          <Box
            sx={{
              width: isMobile
                ? 0
                : isLargeScreen && !preferences.sidebarCollapsed
                  ? `${SIDEBAR_EXPANDED_WIDTH}px`
                  : `${SIDEBAR_COLLAPSED_WIDTH}px`,
              flexDirection: "column",
              flexShrink: 0,
              overflow: "hidden",
              transition: "width 0.3s ease-in-out",
            }}
          >
            <Sidebar
              collapsed={preferences.sidebarCollapsed}
              onToggleCollapse={handleToggleSidebar}
            />
          </Box>
        )}

        {/* Mobile: Sidebar is a drawer, no space needed */}
        {isMobile && (
          <Sidebar
            collapsed={preferences.sidebarCollapsed}
            onToggleCollapse={handleToggleSidebar}
          />
        )}

        {/* Main content area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            backgroundColor: SIDEBAR.bg, // bg-hover
            // On large screens, content width adjusts based on sidebar
            // On small screens, content takes full width (sidebar overlays)
            // On mobile, content takes full width (sidebar is drawer)
            width: isMobile
              ? "100%"
              : isLargeScreen && !preferences.sidebarCollapsed
                ? `calc(100% - ${SIDEBAR_EXPANDED_WIDTH}px)`
                : `calc(100% - ${SIDEBAR_COLLAPSED_WIDTH}px)`,
          }}
        >
          {/* Content - scrollable area */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              margin: "8px",
              backgroundColor: SIDEBAR.itemActiveBg, // bg-bg-primary
              borderRadius: "12px", // rounded-xl
              position: "relative",
              p: 2,
            }}
          >
            <Box
              sx={{
                transition: "margin-right 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                marginRight: {
                  xs: 0,
                  // Content-push for Comments drawer and/or Argus sidebar.
                  // Both can be open simultaneously — stack their widths.
                  // Argus sidebar is hidden on `/argus` itself, so exclude
                  // its width contribution on that route.
                  md: `${
                    (commentsOpen ? COMMENTS_DRAWER_WIDTH : 0) +
                    (argusSidebarOpen && !loc.pathname.startsWith("/argus")
                      ? ARGUS_SIDEBAR_WIDTH
                      : 0)
                  }px`,
                },
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: "100%",
              }}
            >
              <ErrorBoundary>
                {onboardingCompleted === null ? (
                  <FullScreenLoading message="Loading your experience..." />
                ) : !onboardingCompleted && !loc.pathname.startsWith("/argus") ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <Outlet />
                )}
              </ErrorBoundary>
              {/*
                Argus navigate bridge — exposes useNavigate() to the
                Argus connection provider via a ref. Mounted here so it
                always sits inside the router tree. Renders nothing.
              */}
              <ArgusNavigateBridge />
              <Dialog
                open={utmDialogOpen}
                onClose={() => setUtmDialogOpen(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>🎉 Thank you for choosing desplega.ai!</DialogTitle>
                <DialogContent>
                  <Stack spacing={2}>
                    <Typography variant="body1">
                      We&apos;re thrilled to have you on board. Our team is dedicated to helping you
                      unlock the full potential of AI for your applications!
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setUtmDialogOpen(false)}
                      >
                        Got it
                      </Button>
                    </Stack>
                  </Stack>
                </DialogContent>
              </Dialog>
            </Box>
            {
              // eslint-disable-next-line no-constant-binary-expression
              false && <Comments />
            }
          </Box>
          <AskArgusPrompt />
        </Box>
      </Box>
    </Box>
  );
}

export default function Layout() {
  return (
    <CommentsProvider>
      <LayoutContent />
    </CommentsProvider>
  );
}
